import React, { useMemo, useState, useCallback, useEffect } from "react";
import Footer from "../../components/Footer/Footer";
import "../../resources/styling/GlobalStyles.css";
import "./Synergy.css";

// Import catalogue feature helpers
import useCatalogue from "../../features/catalogue/hooks/useCatalogue.js";
import ImageGrid from "../../features/catalogue/components/ImageGrid.js";
import ItemModal from "../../features/catalogue/components/ItemModal.js";
import { loadOverridesForCategory } from "../../features/catalogue/overrides/index.js";

const Synergy = () => {
  const categories = useMemo(
    () => [
      "Bed Frames",
      "Chest Drawers",
      "Shoe Cabinets",
      "Side Table",
      "Sideboards",
      "Wardrobes",
    ],
    []
  );

  // Endpoint selection: use local feed during dev, API in production
  const FEED_URL =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? "/catalogue-feed"
      : "/api/catalogue";

  // Fetch catalogue data
  const { catalogue, loading, error } = useCatalogue(FEED_URL);

  const [openCategory, setOpenCategory] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  const toggle = (cat) => setOpenCategory((prev) => (prev === cat ? null : cat));

  // Open modal and dynamically load overrides for this category
  const openModal = useCallback(async (item) => {
    const overrides = await loadOverridesForCategory(item.category);
    const override = item?.name ? overrides[item.name] : undefined;
    const merged = { ...item, ...override };
    setSelected(merged);

    const sizes = merged?.variants ? Object.keys(merged.variants) : [];
    setSelectedSize(sizes.length ? sizes[0] : null);
  }, []);

  const closeModal = useCallback(() => {
    setSelected(null);
    setSelectedSize(null);
  }, []);

  // Disable body scroll while modal is open
  useEffect(() => {
    if (!selected) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selected]);

  // Close modal on ESC key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeModal]);

  // ---------------------- Render ----------------------
  return (
    <div className="page-container">
      <main className="main-content synergy-wrapper">
        <header className="synergy-header">
          <h1 className="synergy-title">Catalogue</h1>
          <p className="synergy-subtitle">Click a category to view images.</p>
        </header>

        {loading && <p className="loading">Loading catalogue...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && (
          <section className="catalogue-section">
            {categories.map((cat) => {
              const open = openCategory === cat;
              const items = catalogue?.[cat] || [];

              return (
                <div key={cat} className="catalogue-block">
                  <button
                    className={`dropdown-header ${open ? "open" : ""}`}
                    onClick={() => toggle(cat)}
                    aria-expanded={open}
                  >
                    <span>{cat}</span>
                    <span className="chevron" aria-hidden="true">
                      {open ? "▲" : "▼"}
                    </span>
                  </button>

                  <div className={`dropdown-panel ${open ? "open" : ""}`}>
                    {open &&
                      (items.length === 0 ? (
                        <p className="empty">No images yet.</p>
                      ) : (
                        <ImageGrid
                          items={items.map((img) => ({ ...img, category: cat }))}
                          onOpen={openModal}
                        />
                      ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}
      </main>

      {/* Modal */}
      {selected && (
        <ItemModal
          item={selected}
          size={selectedSize}
          setSize={setSelectedSize}
          onClose={closeModal}
        />
      )}

      <Footer />
    </div>
  );
};

export default Synergy;
